import * as React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import UsersIcon from "@mui/icons-material/People";
import ProductionsIcon from "@mui/icons-material/Biotech";
import StudiesIcon from "@mui/icons-material/LibraryBooksOutlined";
import StudyIcon from "@mui/icons-material/QueryStatsOutlined";
import AnalyticsIcon from "@mui/icons-material/InsightsOutlined";
import KitsIcon from "@mui/icons-material/ScienceOutlined";
import CentersIcon from "@mui/icons-material/HomeWorkOutlined";
import SponsorsIcon from "@mui/icons-material/Apartment";
import SampleIcon from "@mui/icons-material/Science";
import DownloadIcon from "@mui/icons-material/Download";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { Tooltip } from "@mui/material";
import { Copyright } from "../Copyright";
import { store } from "../../store";
import { logoutAction } from "../../redux/actions/root";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { Drawer } from "../ui/Drawer";
import AppBarComponent from "../ui/AppBar";
import renderTitle from "./Title";
import { getCenterData } from "../../redux/actions/center";
import HelpIcon from "@mui/icons-material/Help";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { checkStudyExports } from "../../redux/actions/study";
import config from "../../config/config.json";
import packageJson from "../../../package.json";

export const Protected = ({ children }) => {
  // ========================== HOOKS & STATE ==========================
  const { t } = useTranslation();
  const [, setStudySelected] = React.useState(false);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  // -------------------- Role flags & organization --------------------
  const adminUser = user.role === "admin";
  const technicianUser = user.role === "technician";
  const cdlUser = user.organization === "CDL";
  const employeeUser = user.role === "employee" && cdlUser; // VELA
  const canViewSamplesMenu = adminUser || technicianUser; // Temporary menu restriction until Samples supports the other roles.
  const sponsorUser = user.organization === "Sponsor";
  const centerUser = user.organization === "Center";
  const centerManagerUser = centerUser && user.role === "manager";
  const legacyCenterUser = centerUser && !centerManagerUser; // old rôle: center employee
  const monitorUser = user.role === "monitor";
  // --------------------- Routing-derived params ----------------------
  const center = useSelector((state) => state.center.center);
  const [open, setOpen] = React.useState(true);
  const [hasExports, setHasExports] = React.useState(false);
  const [kitsExpanded, setKitsExpanded] = React.useState(false);
  const [samplesExpanded, setSamplesExpanded] = React.useState(false);
  const location = useLocation();
  const accessibleCenters = Array.isArray(user.centers) ? user.centers : [];
  const studyCode = legacyCenterUser
    ? center?.Study?.sponsorStudyCode
    : location.state?.studyCode;
  const studyId =
    location.state?.studyId ||
    (legacyCenterUser ? center?.studyId || center?.Study?.id : undefined);
  const centerNumber = legacyCenterUser
    ? center?.centerNumber
    : location.state?.centerNumber;
  const centerId = legacyCenterUser
    ? location.state?.centerId || user.centerId || accessibleCenters[0]?.id
    : location.state?.centerId;
  const showCenterManagerSelector =
    legacyCenterUser && accessibleCenters.length > 0;
  const isLocalEnv = String(config.environment || "").toLowerCase() === "local";
  const isStagingOrTestingEnv =
    config.environment === "staging" || config.environment === "testing";
  const supportVersionLabel = `v${packageJson.version}`;
  const subMenuItemSx = {
    paddingLeft: 4,
    marginLeft: 0,
    borderLeftWidth: 5,
    borderLeftStyle: "solid",
    borderLeftColor: (theme) =>
      isLocalEnv
        ? "black"
        : isStagingOrTestingEnv
          ? "#1976d2"
          : theme.palette.primary.main,
  };
  const rootMenuItemSx = {
    paddingLeft: 2,
    "& .MuiListItemIcon-root": {
      minWidth: 56,
    },
    "& .MuiListItemText-primary": {
      fontSize: "1.08rem",
    },
  };
  const isAnalysisRoute =
    location.pathname.startsWith("/sample/analysis") ||
    location.pathname === "/analysis";
  const isSampleShipmentsRoute =
    location.pathname.startsWith("/sample-shipments");
  const isSampleSetupRoute = location.pathname === "/sample/setup";
  const isSampleRoute =
    isAnalysisRoute ||
    isSampleSetupRoute ||
    location.pathname.startsWith("/samples") ||
    isSampleShipmentsRoute;
  const hasSampleContext = Boolean(studyCode || centerId || centerNumber);
  const sampleSectionActive =
    canViewSamplesMenu &&
    (samplesExpanded || (hasSampleContext && isSampleRoute));
  const isCenterSampleContext = Boolean(centerId);
  const isStudySampleContext = sampleSectionActive && !isCenterSampleContext;

  // ------------------------ Computed title ---------------------------
  const showTitle = renderTitle(
    location,
    studyCode,
    studyId,
    centerNumber,
    legacyCenterUser,
    centerId,
    t,
    navigate,
  );

  // -------------------------- Async helpers --------------------------
  const hasStudyExports = async (studyId) => {
    try {
      const result = await store.dispatch(checkStudyExports(studyId));
      return result;
    } catch (error) {
      console.error("Error checking study exports:", error);
      return false;
    }
  };

  // ----------------------------- Effects -----------------------------
  React.useEffect(() => {
    const checkStudyExports = async () => {
      if (studyId && !centerId) {
        const result = await hasStudyExports(studyId);
        setHasExports(result);
      }
    };
    checkStudyExports();
  }, [studyId, centerId]);

  React.useEffect(() => {
    if (centerUser) {
      if (centerId) {
        store.dispatch(getCenterData(centerId));
      }
    }
  }, [centerUser, centerId]);

  // ---------------------------- Handlers -----------------------------
  const toggleDrawer = () => {
    setOpen(!open);
  };

  // =============================== RENDER ============================
  if (!user) <CircularProgress />;
  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "grey.100",
      }}>
      <CssBaseline />
      <AppBarComponent
        open={open}
        toggleDrawer={toggleDrawer}
        showTitle={showTitle}
        user={user}
      />
      {!user ? (
        <CircularProgress></CircularProgress>
      ) : (
        <Drawer variant="permanent" open={open}>
          <div>
            {/* ===================== SIDE NAVIGATION ====================== */}
            <Toolbar
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                px: [1],
              }}>
              <IconButton onClick={toggleDrawer}>
                <ChevronLeftIcon />
              </IconButton>
            </Toolbar>

            {/* ======================= MAIN NAV LIST ====================== */}
            <List>
              {/* ========================= STUDIES ========================= */}
              {/* LIST OF STUDIES */}
              {(cdlUser || sponsorUser || centerManagerUser) && (
                <>
                  <ListItemButton
                    selected={location.pathname === "/studies"}
                    onClick={() => {
                      setKitsExpanded(false);
                      setSamplesExpanded(false);
                      navigate("/studies");
                      setStudySelected(!!studyCode);
                    }}
                    sx={rootMenuItemSx}>
                    <ListItemIcon>
                      <StudiesIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("studies")} />
                  </ListItemButton>
                  <Divider />

                  {/* ANALYTICS */}
                  {/* <ListItemButton
                    selected={location.pathname === "/analytic"}
                    onClick={() => {
                      setKitsExpanded(false);
                      setSamplesExpanded(false);
                      navigate("/analytic");
                    }}
                    sx={rootMenuItemSx}>
                    <ListItemIcon>
                      <AnalyticsIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("analytics")} />
                  </ListItemButton>
                  <Divider /> */}
                </>
              )}
              {/* ====================== STUDY SELECTED ===================== */}
              {/* STUDY SELECTED */}
              {studyCode && !legacyCenterUser && (
                <>
                  <ListItemButton
                    onClick={() => {
                      setKitsExpanded(false);
                      setSamplesExpanded(false);
                      navigate(`/centers`, { state: { studyId, studyCode } });
                    }}
                    sx={{ paddingLeft: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ListItemIcon>
                        <StudyIcon />
                      </ListItemIcon>
                      <Typography variant="h6"> {studyCode} </Typography>
                    </Box>
                    {adminUser && (
                      <Tooltip title={t("studyInfo")}>
                        <IconButton
                          size="small"
                          aria-label={t("studyInfo")}
                          onClick={(event) => {
                            event.stopPropagation();
                            setKitsExpanded(false);
                            setSamplesExpanded(false);
                            navigate("/study-info", {
                              state: { studyCode, studyId },
                            });
                          }}
                          sx={{ marginLeft: "auto" }}>
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Divider />
                  </ListItemButton>
                </>
              )}
              {/* ======================= CENTERS LIST ====================== */}
              {/* LIST OF CENTERS */}
              {studyCode &&
                (cdlUser || sponsorUser || centerManagerUser) &&
                !centerNumber &&
                !kitsExpanded &&
                !sampleSectionActive && (
                  <>
                    <Divider />
                    <ListItemButton
                      selected={location.pathname === "/centers"}
                      onClick={() => {
                        navigate(`/centers`, { state: { studyId, studyCode } });
                      }}
                      sx={subMenuItemSx}>
                      <ListItemIcon>
                        <CentersIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("centers")} />
                    </ListItemButton>
                  </>
                )}
              {/* ====================== CENTER SELECTED ==================== */}
              {/*  CENTER SELECTED */}
              {!showCenterManagerSelector && centerNumber && (
                <>
                  <Divider />
                  <ListItemButton
                    onClick={() => {
                      setKitsExpanded(false);
                      setSamplesExpanded(false);
                      navigate("/productions", {
                        state: {
                          centerNumber,
                          studyCode,
                          studyId,
                          centerId,
                        },
                      });
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ListItemIcon>
                        <CentersIcon />
                      </ListItemIcon>
                      <Typography variant="h6">
                        {t("center")} {centerNumber}
                      </Typography>
                    </Box>
                    {adminUser && (
                      <Tooltip title={t("centerInfo")}>
                        <IconButton
                          size="small"
                          aria-label={t("centerInfo")}
                          onClick={(event) => {
                            event.stopPropagation();
                            setKitsExpanded(false);
                            setSamplesExpanded(false);
                            navigate("/center-info", {
                              state: {
                                centerNumber,
                                studyCode,
                                studyId,
                                centerId,
                              },
                            });
                          }}
                          sx={{ marginLeft: "auto" }}>
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemButton>
                </>
              )}
              {showCenterManagerSelector && !sampleSectionActive && (
                <>
                  <Divider />
                  {accessibleCenters.map((accessibleCenter) => {
                    const currentStudyCode =
                      accessibleCenter?.Study?.sponsorStudyCode ||
                      accessibleCenter?.Study?.cdlStudyCode;

                    return (
                      <ListItemButton
                        key={accessibleCenter.id}
                        selected={
                          String(centerId) === String(accessibleCenter.id)
                        }
                        onClick={() => {
                          setKitsExpanded(false);
                          setSamplesExpanded(false);
                          navigate("/productions", {
                            state: {
                              centerId: accessibleCenter.id,
                              centerNumber: accessibleCenter.centerNumber,
                              studyId: accessibleCenter.studyId,
                              studyCode: currentStudyCode,
                            },
                          });
                        }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <ListItemIcon>
                            <CentersIcon />
                          </ListItemIcon>
                          <Typography variant="h6">
                            {t("center")} {accessibleCenter.centerNumber}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    );
                  })}
                </>
              )}
              {/* ========================= KITS MENU ======================= */}
              {/* KITS MENU */}
              {studyCode &&
                !monitorUser &&
                !employeeUser &&
                !centerManagerUser &&
                !kitsExpanded &&
                !sampleSectionActive &&
                !centerId && (
                  <>
                    <Divider />
                    <ListItemButton
                      selected={location.pathname === "/materials"}
                      onClick={() => {
                        setKitsExpanded(true);
                        setSamplesExpanded(false);
                        navigate("/materials", {
                          state: { studyId, studyCode, centerNumber, centerId },
                        });
                      }}
                      sx={subMenuItemSx}>
                      <ListItemIcon>
                        <KitsIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("kits")} />
                    </ListItemButton>
                  </>
                )}

              {kitsExpanded &&
                !sampleSectionActive &&
                !centerId &&
                !monitorUser && (
                  <>
                    <Divider />
                    <ListItemButton
                      onClick={() => {
                        setKitsExpanded(false);
                        navigate("/centers", {
                          state: { studyId, studyCode },
                        });
                      }}
                      sx={{ paddingLeft: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ListItemIcon>
                          <KeyboardReturnIcon />
                        </ListItemIcon>
                        <Typography variant="h6"> {t("kits")} </Typography>
                      </Box>
                    </ListItemButton>
                  </>
                )}

              {/* ======================= KITS SUBMENUS ===================== */}
              {/* Material list, Productions, Orders */}
              {!sampleSectionActive &&
                ((kitsExpanded && !monitorUser) || centerId) && (
                  <>
                    <Divider sx={{ opacity: 0.3 }} />
                    <ListItemButton
                      selected={location.pathname === "/materials"}
                      onClick={() => {
                        navigate("/materials", {
                          state: {
                            studyId,
                            studyCode,
                            centerNumber,
                            centerId,
                          },
                        });
                      }}
                      sx={subMenuItemSx}>
                      <ListItemIcon>
                        <KitsIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("materialList")} />
                    </ListItemButton>
                  </>
                )}

              {/* ======================= PRODUCTIONS ======================= */}
              {/* PRODUCTIONS MENU */}
              {!sampleSectionActive &&
                ((kitsExpanded && !monitorUser) || centerId) && (
                  <>
                    <Divider sx={{ opacity: 0.3 }} />
                    <ListItemButton
                      selected={location.pathname === "/productions"}
                      onClick={() => {
                        navigate("/productions", {
                          state: {
                            studyId,
                            studyCode,
                            centerNumber,
                            centerId,
                          },
                        });
                      }}
                      sx={subMenuItemSx}>
                      <ListItemIcon>
                        <ProductionsIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          centerUser || location.state?.centerId
                            ? t("inventory")
                            : t("productions")
                        }
                      />
                    </ListItemButton>
                  </>
                )}
              {/* =========================== ORDERS ======================== */}
              {!sampleSectionActive &&
                (kitsExpanded || centerId) &&
                !employeeUser && (
                  <>
                    <Divider sx={{ opacity: 0.3 }} />
                    <ListItemButton
                      selected={
                        location.pathname === "/orders/" ||
                        location.pathname === "/order"
                      }
                      onClick={() => {
                        navigate("/orders/", {
                          state: {
                            centerNumber,
                            studyCode,
                            studyId,
                            centerId,
                          },
                        });
                      }}
                      sx={subMenuItemSx}>
                      <ListItemIcon>
                        <ShoppingCartIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("kitOrders")} />
                    </ListItemButton>
                  </>
                )}
              {/* Sample module access: admins and technicians only. */}
              {studyCode &&
                !kitsExpanded &&
                !sampleSectionActive &&
                !centerId &&
                canViewSamplesMenu && (
                  <>
                    <Divider />
                    <ListItemButton
                      onClick={() => {
                        setKitsExpanded(false);
                        setSamplesExpanded(true);
                        navigate("/sample-shipments", {
                          state: {
                            studyCode,
                            studyId,
                          },
                        });
                      }}
                      sx={subMenuItemSx}>
                      <ListItemIcon>
                        <SampleIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("samples")} />
                    </ListItemButton>
                  </>
                )}
              {/* Keep the center menu for future support of other roles. */}
              {centerId && canViewSamplesMenu && !sampleSectionActive && (
                <>
                  <Divider />
                  <ListItemButton
                    onClick={() => {
                      setKitsExpanded(false);
                      setSamplesExpanded(true);
                      navigate("/sample-shipments", {
                        state: {
                          centerNumber,
                          studyCode,
                          studyId,
                          centerId,
                        },
                      });
                    }}
                    sx={subMenuItemSx}>
                    <ListItemIcon>
                      <SampleIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("samples")} />
                  </ListItemButton>
                </>
              )}

              {/* ========================= STUDY INFO ====================== */}
              {adminUser &&
                studyCode &&
                !centerNumber &&
                !kitsExpanded &&
                !sampleSectionActive && (
                  <>
                    <Divider />
                    <ListItemButton
                      selected={location.pathname === "/study-info"}
                      onClick={() => {
                        navigate("/study-info", {
                          state: { studyCode, studyId },
                        });
                      }}
                      sx={subMenuItemSx}>
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("studyInfo")} />
                    </ListItemButton>
                    <Divider />
                  </>
                )}
              {/* =========================== EXPORTS ======================= */}
              {studyCode &&
                !centerNumber &&
                hasExports &&
                !kitsExpanded &&
                !sampleSectionActive && (
                  <>
                    <ListItemButton
                      selected={location.pathname === "/exports"}
                      onClick={() => {
                        setKitsExpanded(false);
                        setSamplesExpanded(false);
                        navigate("/exports", {
                          state: { studyCode, studyId },
                        });
                      }}
                      sx={subMenuItemSx}>
                      <ListItemIcon>
                        <DownloadIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("exports")} />
                    </ListItemButton>
                    <Divider />
                  </>
                )}

              {sampleSectionActive && (
                <>
                  <Divider />
                  <ListItemButton
                    onClick={() => {
                      setSamplesExpanded(false);
                      if (centerId) {
                        navigate("/productions", {
                          state: {
                            centerNumber,
                            studyCode,
                            studyId,
                            centerId,
                          },
                        });
                      } else {
                        navigate("/centers", {
                          state: { studyId, studyCode },
                        });
                      }
                    }}
                    sx={{ paddingLeft: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ListItemIcon>
                        <KeyboardReturnIcon />
                      </ListItemIcon>
                      <Typography variant="h6"> {t("samples")} </Typography>
                    </Box>
                    {adminUser && isStudySampleContext && (
                      <Tooltip title={t("settings", "Settings")}>
                        <IconButton
                          size="small"
                          aria-label={t("settings", "Settings")}
                          onClick={(event) => {
                            event.stopPropagation();
                            setKitsExpanded(false);
                            setSamplesExpanded(true);
                            navigate("/sample/setup", {
                              state: { studyCode, studyId },
                            });
                          }}
                          sx={{ marginLeft: "auto" }}>
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemButton>
                </>
              )}

              {/* ======================= SAMPLES SUBMENUS =================== */}
              {/* {sampleSectionActive &&
                isCenterSampleContext &&
                !technicianUser &&
                !employeeUser && (
                  <>
                    <Divider sx={{ opacity: 0.3 }} />
                    <ListItemButton
                      selected={location.pathname.startsWith("/samples")}
                      onClick={() => {
                        navigate("/samples", {
                          state: {
                            centerNumber,
                            studyCode,
                            studyId,
                            centerId,
                          },
                        });
                      }}
                      sx={subMenuItemSx}>
                      <ListItemIcon>
                        <SampleIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("samples")} />
                    </ListItemButton>
                  </>
                )} */}

              {/* ======================= SAMPLES SUBMENUS =================== */}
              {sampleSectionActive && (
                <>
                  <Divider sx={{ opacity: 0.3 }} />
                  <ListItemButton
                    selected={isSampleShipmentsRoute}
                    onClick={() => {
                      navigate("/sample-shipments", {
                        state: {
                          centerNumber: isCenterSampleContext
                            ? centerNumber
                            : undefined,
                          studyCode,
                          studyId,
                          centerId: isCenterSampleContext
                            ? centerId
                            : undefined,
                        },
                      });
                    }}
                    sx={subMenuItemSx}>
                    <ListItemIcon>
                      <SampleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("sampleOrders", "Sample Orders")}
                    />
                  </ListItemButton>
                </>
              )}

              {/* ======================= SAMPLES SUBMENUS =================== */}
              {isStudySampleContext && adminUser && (
                <>
                  <ListItemButton
                    selected={isSampleSetupRoute || isAnalysisRoute}
                    onClick={() => {
                      navigate("/sample/setup", {
                        state: {
                          studyCode,
                          studyId,
                        },
                      });
                    }}
                    sx={subMenuItemSx}>
                    <ListItemIcon>
                      <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("settings", "Settings")} />
                  </ListItemButton>
                </>
              )}

              {/* ========================= CENTER INFO ===================== */}
              {adminUser && centerNumber && !sampleSectionActive && (
                <>
                  <Divider />
                  <ListItemButton
                    selected={location.pathname === "/center-info"}
                    onClick={() => {
                      navigate(`/center-info`, {
                        state: {
                          centerNumber,
                          studyCode,
                          studyId,
                          centerId,
                        },
                      });
                    }}
                    sx={subMenuItemSx}>
                    <ListItemIcon>
                      <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("centerInfo")} />
                  </ListItemButton>
                  <Divider />
                </>
              )}
              {/* ====================== ADMIN ROOT MENUS =================== */}
              {/* sponsors, users, global orders, shipment orders */}
              {adminUser && !studyCode && (
                <>
                  <ListItemButton
                    selected={location.pathname === "/sponsors"}
                    onClick={() => {
                      navigate("/sponsors");
                      setStudySelected(false);
                    }}
                    sx={rootMenuItemSx}>
                    <ListItemIcon>
                      <SponsorsIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("sponsors")} />
                  </ListItemButton>
                  <Divider />
                  <ListItemButton
                    selected={location.pathname === "/users"}
                    onClick={() => {
                      navigate("/users");
                      setStudySelected(false);
                    }}
                    sx={rootMenuItemSx}>
                    <ListItemIcon>
                      <UsersIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("users")} />
                  </ListItemButton>
                  <Divider />
                </>
              )}
              {(adminUser || technicianUser) && !studyCode && (
                <>
                  <ListItemButton
                    selected={location.pathname === "/orders"}
                    onClick={() => {
                      setKitsExpanded(false);
                      setSamplesExpanded(false);
                      navigate("/orders");
                    }}
                    sx={rootMenuItemSx}>
                    <ListItemIcon>
                      <ShoppingCartIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("kitOrders")} />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {canViewSamplesMenu && !hasSampleContext && (
                <>
                  <ListItemButton
                    selected={location.pathname.startsWith("/sample-shipments")}
                    onClick={() => {
                      setKitsExpanded(false);
                      setSamplesExpanded(false);
                      navigate("/sample-shipments", { state: null });
                    }}
                    sx={rootMenuItemSx}>
                    <ListItemIcon>
                      <SampleIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("sampleOrders")} />
                  </ListItemButton>
                  <Divider />
                </>
              )}
            </List>
          </div>

          {/* ===================== SUPPORT & LOGOUT ====================== */}
          <div>
            <LanguageSwitcher />
            <Divider />
            <ListItemButton
              onClick={() => {
                window.open(
                  "https://ablsa.atlassian.net/servicedesk/customer/portal/23",
                  "_blank",
                );
              }}
              sx={{ paddingLeft: 4, paddingRight: 2 }}>
              <ListItemIcon>
                <HelpIcon />
              </ListItemIcon>
              <Tooltip title={t("sendTicketToCustomerSupport")}>
                <ListItemText primary={t("support")} />
              </Tooltip>
              <Chip
                label={supportVersionLabel}
                size="small"
                sx={{
                  marginLeft: "auto",
                  color: "text.secondary",
                  backgroundColor: (theme) => theme.palette.action.hover,
                  opacity: 0.85,
                  height: 22,
                }}
              />
            </ListItemButton>
            <Divider />
            <ListItemButton
              onClick={() => {
                navigate("/");
                store.dispatch(logoutAction());
              }}
              sx={{ paddingLeft: 4 }}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t("logout")} />
            </ListItemButton>
          </div>
        </Drawer>
      )}
      {/* ============================== CONTENT ======================== */}
      <Box
        component="main"
        sx={{
          backgroundColor: "grey.100",
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
        }}>
        <Toolbar />
        <Container
          maxWidth="xl"
          sx={{
            pt: 2,
            mb: 4,
          }}>
          <Grid container>
            <Outlet />
          </Grid>
          <Copyright sx={{ pt: 4 }} />
        </Container>
      </Box>
    </Box>
  );
};
